import { ValidationError } from 'yup'
import { ActionResult, ErrorType, RequestError, TSError } from '@/types/errors.types'
import { TicketureErrorResponse } from '@/types/ticketure-api.types'
import { ERROR_MODAL_MESSAGES, ErrorsConfig, IGNORED_USER_ERRORS } from '@/messages/errors'

export class AppError extends Error {
  constructor(
    public readonly errorType: ErrorType,
    message?: string,
  ) {
    super(message)
    this.name = 'AppError'
  }
}

export const isAppError = (err: unknown): err is AppError => err instanceof AppError

/**
 * Thrown when Ticketure has no seller record for a derived slug. Not every venue has a
 * sibling events seller, so callers that merely probe for one (e.g. the events-during-visit
 * widget) can treat this as "nothing here" instead of reporting it as a failure.
 */
export class SellerNotFoundError extends Error {
  constructor(public readonly sellerSlug: string) {
    super(`Seller not found for slug: ${sellerSlug}`)
    this.name = 'SellerNotFoundError'
  }
}

export const isSellerNotFoundError = (err: unknown): err is SellerNotFoundError => (
  err instanceof SellerNotFoundError
)

/**
 * A request for an event Ticketure has no record of: either the API 404s on the slug or the
 * response carries no matching event. A dead link or a stale crawl, not a defect, so callers
 * render the 404 page instead of reporting one issue per hit.
 */
export const isEventNotFoundError = (err: unknown): boolean => (
  (err as RequestError)?.response?.status === 404
  || (err instanceof Error && err.message === 'Event not found')
)

export const unwrapActionResult = <T>(result: ActionResult<T>): T => {
  if (result.error !== null) throw new AppError(result.error, result.message)
  return result.data
}

export const tsErrors: TSError[] = [
  {
    msgs: [
      '402: Payment Required',
    ],
    type: ErrorType.ProcessingCard,
  },
]

export const matchTsError = (errorMsg: string): TSError | undefined => {
  const incomingMsg = errorMsg.toLowerCase()
  const matchedError = tsErrors.filter((err) => {
    let matched = false
    err.msgs.forEach((msg) => {
      if (incomingMsg.indexOf(msg.toLowerCase()) > -1) {
        matched = true
      }
    })
    return matched
  })
  return matchedError.pop()
}

const isTicketureErrorBody = (data: unknown): data is TicketureErrorResponse => (
  !!data
  && typeof data === 'object'
  && '_data' in data
  // eslint-disable-next-line no-underscore-dangle
  && Array.isArray((data as TicketureErrorResponse)._data)
)

/**
 * Maps Ticketure error `_code`  values to a map of Ticketure _context fragments and app ErrorTypes so specific API
 * failures (e.g. capacity errors) can surface tailored messaging.
 */
export const ticketureCodeAndContextMap = new Map<string, Map<string, ErrorType>>([
  ['session_capacity_exceeded', new Map([ ['any', ErrorType.CapacityExceeded ] ])],
  ['ticket_group_capacity_exceeded', new Map([ ['any', ErrorType.CapacityExceeded ] ])],
  ['ticket_group_sold_out', new Map([[ 'any', ErrorType.CapacityExceeded ]])],
  ['event_template_hidden', new Map([[ 'any', ErrorType.ModifyCart ]])],
  ['not_found', new Map<string, ErrorType>([
    [ 'cart.*', ErrorType.CartNotFound ],
    [ 'event_template.*', ErrorType.EventNotFound ],
    [
      'events/(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|available)(?:\\?.*)?$',
      ErrorType.EventNotFound,
    ],
    [ 'events/.*/sessions', ErrorType.SessionNotFound ],
  ])],
])

/**
 * Pulls the first Ticketure error datum off a thrown request error, if present.
 * Request errors thrown by api-request.service carry the parsed body on
 * `err.response.data`.
 */
export const getTicketureErrorData = (err: unknown): TicketureErrorResponse['_data'][number] | undefined => {
  const data = (err as RequestError)?.response?.data
  // eslint-disable-next-line no-underscore-dangle
  return isTicketureErrorBody(data) ? data._data[0] : undefined
}

/**
 * Resolves the ErrorType mapped for a given Ticketure `_context`, given the code's map of
 * context fragments to ErrorTypes. 'any' is a wildcard used by codes whose messaging doesn't
 * depend on the _context (e.g. capacity errors); other keys are matched as _context substrings.
 */
const resolveContextErrorType = (
  contextMap: Map<string, ErrorType>,
  context: string,
): ErrorType | undefined => {
  const needle = Array.from(contextMap.keys()).find((key) => {
    const regex = new RegExp(key, 'gi')
    return key === 'any' || context.match(regex)
  })
  return needle !== undefined ? contextMap.get(needle) : undefined
}

/**
 * Normalizes an unknown thrown value into an AppError when possible:
 * - already-AppError → returned as-is
 * - request error carrying a mapped Ticketure `_code` → AppError with the mapped ErrorType
 * - Error whose message matches a known TS error → AppError with the mapped ErrorType
 * - anything else → null (caller decides: rethrow, fallback, etc.)
 */
export const toAppError = (err: unknown, allowAllErrors = false): AppError | null => {
  if (isAppError(err)) return err

  // Check the API's machine-readable `_code` before the message-string match below:
  // codes are stable across copy changes and unambiguous about which failure occurred.
  const datum = getTicketureErrorData(err)
  // eslint-disable-next-line no-underscore-dangle
  const contextMap = datum && ticketureCodeAndContextMap.get(datum._code)

  if (datum && allowAllErrors) {
    return new AppError(
      ErrorType.Generic,
      // eslint-disable-next-line no-underscore-dangle
      datum._description || ERROR_MODAL_MESSAGES.connection,
    )
  }

  if (datum && contextMap !== undefined) {
    // eslint-disable-next-line no-underscore-dangle
    const mappedType = resolveContextErrorType(contextMap, datum._context)

    // Compare against undefined rather than truthiness: ErrorType is a numeric enum whose
    // first member (ErrorTypeNotFound) is 0, a valid mapping target that is also falsy.
    if (mappedType !== undefined) {

      return new AppError(
        mappedType,

        ErrorsConfig.has(mappedType)
          // eslint-disable-next-line no-underscore-dangle
          ? (ErrorsConfig.get(mappedType)?.title || datum._description)
          // eslint-disable-next-line no-underscore-dangle
          : datum._description,
      )
    }
  }

  if (err instanceof Error) {
    const matched = matchTsError(err.message)
    if (matched) return new AppError(matched.type, err.message)
  }
  return null
}

export const extractValidationErrors = (err: unknown): Record<string, string> => {
  if (err instanceof ValidationError) {
    const errors: Record<string, string> = {}
    err.inner.forEach((e) => {
      if (e.path) errors[e.path] = e.message
    })
    return errors
  }
  return {}
}

/**
 * Checks a create/update user response for errors, filtering out known ignorable errors.
 * Returns an error message if the response indicates a failure, or null if the response is ok.
 */
export const getErrorFromUserResponse = (
  response: Transformed.CreateUserResponse | undefined,
): string | null => {
  if (!response) return null
  if (response.status === 'FAILURE') {
    const message = response.message ?? ''
    if (IGNORED_USER_ERRORS.some((ignored) => message.includes(ignored))) {
      return null
    }
    return message || null
  }
  return null
}
